import type { AnimationI, AnySceneObjectT, TextI, TickerI } from "tweenly"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import * as tweenly from "tweenly"
import { Asset } from "tweenly/dist/fetchAsset"
import { v4 as uuidv4 } from "uuid"
import { createObject, sortObjects } from "tweenly"
import { del } from "../../helpers/ajax.helpers"
import { S3_PREFIX } from "../../helpers/file.helpers"
import { ObjectStyles, hasStaticDescendant } from "../../helpers/object.helpers"
import { selectUndoable } from "../../helpers/selector.helpers"
import { useToast } from "../../hooks/useToast"
import { AppThunkT } from "../store"
import { setActiveObjectAction, setSelectedObjectIdsAction } from "./activeObject.slice"
import { addAnimationBaseAction } from "./objects.slice/animation/addAnimationBaseAction"
import { addAutoAnimationsBaseAction } from "./objects.slice/animation/addAutoAnimationsBaseAction"
import { createSequenceAnimationsBaseAction } from "./objects.slice/animation/createSequenceAnimationsBaseAction"
import { deleteAnimationsBaseAction } from "./objects.slice/animation/deleteAnimationsBaseAction"
import { duplicateAnimationsBaseAction } from "./objects.slice/animation/duplicateAnimationsBaseAction"
import { durationAnimationBaseAction } from "./objects.slice/animation/durationAnimationBaseAction"
import { durationAnimationsBaseAction } from "./objects.slice/animation/durationAnimationsBaseAction"
import { moveOffsetAnimationsBaseAction } from "./objects.slice/animation/moveOffsetAnimationsBaseAction"
import { relativeDurationAnimationBaseAction } from "./objects.slice/animation/relativeDurationAnimationBaseAction"
import { relativeDurationAnimationsBaseAction } from "./objects.slice/animation/relativeDurationAnimationsBaseAction"
import { startOffsetAnimationBaseAction } from "./objects.slice/animation/startOffsetAnimationBaseAction"
import { startOffsetAnimationsBaseAction } from "./objects.slice/animation/startOffsetAnimationsBaseAction"
import { updateAnimationsBaseAction } from "./objects.slice/animation/updateAnimationBaseAction"
import { createImageBaseAction } from "./objects.slice/object/createImageBaseAction"
import { createTickerStructureBaseAction } from "./objects.slice/object/createTickerStructureBaseAction"
import { duplicateObjectsBaseAction } from "./objects.slice/object/duplicateObjectsBaseAction"
import { moveObjectsBaseAction } from "./objects.slice/object/moveObjectsBaseAction"
import { moveSelectedHereBaseAction } from "./objects.slice/object/moveSelectedHereBaseAction"
import { moveSelectedToRootBaseAction } from "./objects.slice/object/movetSelectedToRootBaseAction"
import { objectNodeUpdate } from "./objects.slice/object/objectNodeUpdate"
import {
    CreateImageT,
    PayloadAnimationT,
    PayloadChildT,
    PayloadIndexT,
    PayloadObjDelT,
    PayloadObjectsT,
    PayloadObjectT,
    PayloadObjT,
    PayloadParentT,
    PayloadStyledT,
} from "./objects.slice/object/objectTypes"
import { updateObjectLockAspectRationPropertyBaseAction } from "./objects.slice/object/updateObjectLockAspectRationPropertyBaseAction"
import { updateObjectsDifferentStylesProperty } from "./objects.slice/object/updateObjectsDifferentStylesProperty"
import { updateObjectsPosition } from "./objects.slice/object/updateObjectsPosition"
import { updateObjectsStyleProperty } from "./objects.slice/object/updateObjectsStyleProperty"
import { updateObjectVisibilityStylesBaseAction } from "./objects.slice/object/updateObjectVisibilityStylesBaseAction"
import {
    defaultObject,
    uploadObjectsAndAnimationsBaseAction,
} from "./objects.slice/object/uploadObjectsAndAnimationsBaseAction"

import { ObjectStylesWithId } from "../../hooks/editor/useWrapSelectedObjects"
import { groupObjectsBaseAction } from "./objects.slice/object/groupObjectsBaseAction"
import { unGroupObjectsBaseAction } from "./objects.slice/object/unGroupObjectsBaseAction"
import { updateObjectsLockAspectRationPropertyBaseAction } from "./objects.slice/object/updateObjectsLockAspectRationPropertyBaseAction"
import { updateObjectsStylesBaseAction } from "./objects.slice/object/updateObjectsStylesBaseAction"
import { addSubTimelineAnimationBaseAction } from "./objects.slice/ticker/addSubTimelineAnimationBaseAction"
import { addTextCrawlContinuousBaseAction } from "./objects.slice/ticker/addTextCrawlContinuousBaseAction"
import { addTextCrawlStaggeredBaseAction } from "./objects.slice/ticker/addTextCrawlStaggeredBaseAction"
import { addTextScrollDownBaseAction } from "./objects.slice/ticker/addTextScrollDownBaseAction"
import { addTextScrollUpBaseAction } from "./objects.slice/ticker/addTextScrollUpBaseAction"
import { addTextScrollUpDownBaseAction } from "./objects.slice/ticker/addTextScrollUpDownBaseAction"
import { deleteTickerTextBaseAction } from "./objects.slice/ticker/deleteTickerTextBaseAction"
import { resizeCrawlTickerStylesBaseAction } from "./objects.slice/ticker/resizeCrawlTickerStylesBaseAction"
import { resizeScrollTickerStylesBaseAction } from "./objects.slice/ticker/resizeScrollTickerStylesBaseAction"
import { updateCrawlPropertyBaseAction } from "./objects.slice/ticker/updateCrawlPropertyBaseAction"
import { updateScrollPropertyBaseAction } from "./objects.slice/ticker/updateScrollPropertyBaseAction"
import { updateTextCrawlBaseAction } from "./objects.slice/ticker/updateTextCrawlBaseAction"
import { updateTickerAnimationBaseAction } from "./objects.slice/ticker/updateTickerAnimationBaseAction"
import { updateTickerTextsAttributeBaseAction } from "./objects.slice/ticker/updateTickerTextsIsEditableBaseAction"
import { updateTickerTextsPropertyBaseAction } from "./objects.slice/ticker/updateTickerTextsPropertyBaseAction"
import { updateTickerStyleWithoutRestartBaseAction } from "./objects.slice/ticker/updateTickerStyleWithoutRestartBaseAction"
import { updateTickerObjectWithoutRestartBaseAction } from "./objects.slice/ticker/updateTickerObjectWithoutRestartBaseAction"
import {
    updateTickerTextsStyleWithoutRestartBaseAction,
    UpdateTickerTextsStylePayload,
} from "./objects.slice/ticker/updateTickerTextsStyleWithoutRestartBaseAction"
import { updateTickerCopiesStyles } from "../../helpers/ticker/updateTickerCopiesStyles"
import { markObjectsUpdated } from "./objects.slice/object/markObjectsUpdated"

export type ObjectsStateT = {
    value: Record<string, AnySceneObjectT>
    tmpIds: string[]
    updatedAt: number
    /**
     * Objects touched by the change that set `updatedAt`, or `null` when the scope is not
     * known. See `markObjectsUpdated`.
     */
    lastChangedIds: string[] | null
}

const initialState: ObjectsStateT = {
    value: {},
    tmpIds: [],
    updatedAt: 0,
    lastChangedIds: null,
}

//////////////////animations Types////////////
export type AnimationAddT = {
    animation: AnimationI
}
export type AnimationUpdT = {
    id: string
    objectId: string
    property: string
    value?: any
}

export type AnimationsT = {
    animations: AnimationI[]
}

const objectNodeUpdIndex = (stateObject: any, action: PayloadIndexT): AnySceneObjectT => {
    return {
        ...stateObject,
        index: action.index,
    }
}

const objectNodeUpdParentId = (stateObject: any, action: PayloadParentT): AnySceneObjectT => {
    return {
        ...stateObject,
        parentId: action.parentId,
    }
}

const objectNodeAddChildId = (stateObject: any, action: PayloadChildT): AnySceneObjectT => {
    if (stateObject.childIds.includes(action.childId)) return stateObject
    return {
        ...stateObject,
        childIds: [...stateObject.childIds, action.childId],
    }
}

const objectNodeDelChildId = (stateObject: any, action: PayloadChildT): AnySceneObjectT => {
    return {
        ...stateObject,
        childIds: stateObject.childIds.filter((id: string) => id !== action.childId),
    }
}

const objectNodeDelAnimationId = (stateObject: any, action: PayloadAnimationT): AnySceneObjectT => {
    return {
        ...stateObject,
        animationIds: stateObject.animationIds.filter((id: string) => id !== action.animationId),
    }
}

const objectNodeAddGroupStyleId = (stateObject: any, action: PayloadStyledT): AnySceneObjectT => {
    return {
        ...stateObject,
        styleIds: [...stateObject.groupStyleIds, action.styleId],
    }
}

const objectNodeDelGroupStyleId = (stateObject: any, action: PayloadStyledT): AnySceneObjectT => {
    return {
        ...stateObject,
        styleIds: stateObject.styleIds.filter((id: string) => id !== action.styleId),
    }
}

export const getAllDescendantIds = (state: ObjectsStateT, nodeId: string): any => {
    return state.value[nodeId].childIds.reduce((acc: any, childId: string) => {
        return [...acc, childId, ...getAllDescendantIds(state, childId)]
    }, [])
}

const deleteMany = (state: ObjectsStateT, ids: string[]) => {
    //state = { ...state }
    ids.forEach((id) => {
        if (state) delete state.value[id]
    })
    return state
}

export const objectsSlice = createSlice({
    name: "objects",
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        pushTmpIds: (state, action: PayloadAction<string[]>) => {
            state.tmpIds = [...state.tmpIds, ...action.payload]
        },
        resetTmpIds: (state) => {
            state.tmpIds = []
        },
        uploadObjectsAndAnimationsAction: uploadObjectsAndAnimationsBaseAction,
        addObjAction: (state, action: PayloadAction<PayloadObjectT>) => {
            const id: string = action.payload.object.id
            state.value = {
                ...state.value,
                [id]: action.payload.object,
            }
        },
        addMultiObjAction: (state, action: PayloadAction<PayloadObjectsT>) => {
            action.payload.objects.forEach((object: AnySceneObjectT) => {
                state.value = {
                    ...state.value,
                    [object.id]: object,
                }
                if (object.parentId) {
                    state.value = {
                        ...state.value,
                        [object.parentId]: objectNodeAddChildId(state.value[object.parentId], {
                            id: object.parentId,
                            childId: object.id,
                        }),
                    }
                }
            })
        },
        delMultiObjAction: (state, action: PayloadAction<PayloadObjectsT>) => {
            action.payload.objects.forEach((object: AnySceneObjectT) => {
                if (object.parentId) {
                    state.value = {
                        ...state.value,
                        [object.parentId]: objectNodeDelChildId(state.value[object.parentId], {
                            id: object.parentId,
                            childId: object.id,
                        }),
                    }
                }
                const descendantIds = getAllDescendantIds(state, object.id)

                state = deleteMany(state, [object.id, ...descendantIds])
                //TO DO - after union objects and animations - dispatch(delAnimationsAction({ animationIds }))
            })
        },
        updObjectAction: (state, action: PayloadAction<PayloadObjT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeUpdate(state.value[id], action.payload),
            }
            markObjectsUpdated(state, [id])
        },
        setObjectsExpandedAction: (state, action: PayloadAction<boolean>) => {
            state.value = Object.fromEntries(
                Object.entries(state.value).map(([id, object]) => [
                    id,
                    object.type !== "ticker" && object.childIds.length > 0
                        ? { ...object, isExpanded: action.payload }
                        : object,
                ])
            )
            markObjectsUpdated(state, null)
        },
        updateObjectStylesAction: updateObjectsStylesBaseAction,
        updateObjectsStylePropertyAction: updateObjectsStyleProperty,
        updateObjectsDifferentStylesPropertyAction: updateObjectsDifferentStylesProperty,
        updateObjectsPositionAction: updateObjectsPosition,
        updateObjectVisibilityStylesAction: updateObjectVisibilityStylesBaseAction,
        updateObjectLockAspectRationPropertyAction: updateObjectLockAspectRationPropertyBaseAction,
        updateObjectsLockAspectRationPropertyAction:
            updateObjectsLockAspectRationPropertyBaseAction,
        duplicateObjectsAction: duplicateObjectsBaseAction,
        groupObjectsAction: groupObjectsBaseAction,
        unGroupObjectsAction: unGroupObjectsBaseAction,
        updIndexAction: (state, action: PayloadAction<PayloadIndexT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeUpdIndex(state.value[id], action.payload),
            }
        },
        updParentIdAction: (state, action: PayloadAction<PayloadParentT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeUpdParentId(state.value[id], action.payload),
            }
        },
        addChildIdAction: (state, action: PayloadAction<PayloadChildT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeAddChildId(state.value[id], action.payload),
            }
        },
        delChildIdAction: (state, action: PayloadAction<PayloadChildT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeDelChildId(state.value[id], action.payload),
            }
        },
        delAnimationIdAction: (state, action: PayloadAction<PayloadAnimationT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeDelAnimationId(state.value[id], action.payload),
            }
        },
        addGroupStyleIdAction: (state, action: PayloadAction<PayloadStyledT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeAddGroupStyleId(state.value[id], action.payload),
            }
        },
        delGroupStyleIdAction: (state, action: PayloadAction<PayloadStyledT>) => {
            const id = action.payload.id
            state.value = {
                ...state.value,
                [id]: objectNodeDelGroupStyleId(state.value[id], action.payload),
            }
        },
        delObjectAction: (state, action: PayloadAction<PayloadObjDelT>) => {
            const id = action.payload.id
            const descendantIds = getAllDescendantIds(state, id)

            state = deleteMany(state, [id, ...descendantIds])
        },
        clearObjectsAction: (state) => {
            state.value = {}
        },
        moveSelectedHereAction: moveSelectedHereBaseAction,
        moveObjectsAction: moveObjectsBaseAction,
        moveSelectedToRootAction: moveSelectedToRootBaseAction,
        /////////////////////////////animations action/////////////////////////////
        addAnimationAction: addAnimationBaseAction,
        addAutoAnimationsAction: addAutoAnimationsBaseAction,
        updateAnimationAction: updateAnimationsBaseAction,
        createImageAction: createImageBaseAction,
        createSequenceAnimationsAction: createSequenceAnimationsBaseAction,
        durationAnimationAction: durationAnimationBaseAction,
        durationAnimationsAction: durationAnimationsBaseAction,
        relativeDurationAnimationAction: relativeDurationAnimationBaseAction,
        relativeDurationAnimationsAction: relativeDurationAnimationsBaseAction,
        startOffsetAnimationAction: startOffsetAnimationBaseAction,
        startOffsetAnimationsAction: startOffsetAnimationsBaseAction,
        moveOffsetAnimationsAction: moveOffsetAnimationsBaseAction,
        duplicateAnimationsAction: duplicateAnimationsBaseAction,
        deleteAnimationsAction: deleteAnimationsBaseAction,
        // tickers Action
        createTickerStructureAction: createTickerStructureBaseAction,
        addSubTimelineAnimationAction: addSubTimelineAnimationBaseAction,
        updateTickerAnimationAction: updateTickerAnimationBaseAction,
        addTextScrollUpDownAction: addTextScrollUpDownBaseAction,
        addTextScrollUpAction: addTextScrollUpBaseAction,
        addTextScrollDownAction: addTextScrollDownBaseAction,
        addTextCrawlContinuousAction: addTextCrawlContinuousBaseAction,
        addTextCrawlStaggeredAction: addTextCrawlStaggeredBaseAction,
        updateScrollPropertyAction: updateScrollPropertyBaseAction,
        updateCrawlPropertyAction: updateCrawlPropertyBaseAction,
        updateTickerTextsPropertyAction: updateTickerTextsPropertyBaseAction,
        updateTickerTextsAttributeAction: updateTickerTextsAttributeBaseAction,
        updateTickerStyleWithoutRestartAction: updateTickerStyleWithoutRestartBaseAction,
        updateTickerObjectWithoutRestartAction: updateTickerObjectWithoutRestartBaseAction,
        updateTickerTextsStyleWithoutRestartAction: updateTickerTextsStyleWithoutRestartBaseAction,
        deleteTickerTextAction: deleteTickerTextBaseAction,
        updateTextCrawlAction: updateTextCrawlBaseAction,
        resizeCrawlTickerStylesAction: resizeCrawlTickerStylesBaseAction,
        resizeScrollTickerStylesAction: resizeScrollTickerStylesBaseAction,
    },
})
export const updateTextCrawlActionAsync =
    (
        object: TickerI,
        maskObject: TickerI,
        textObjects: TextI[],
        id: string,
        property: string,
        value: string
    ): AppThunkT =>
    async (dispatch, getState) => {
        const { assets } = getState()
        const newText = await tweenly.processField(value, assets.data as Asset[])

        if (newText)
            dispatch(
                updateTextCrawlAction({
                    object,
                    maskObject,
                    textObjects,
                    id,
                    property,
                    value,
                    newText,
                })
            )
    }
export const updateObjectStylesActionAsync =
    (objects: AnySceneObjectT[], onSubmit: () => void): AppThunkT =>
    async (dispatch) => {
        dispatch(updateObjectStylesAction({ objects }))
        onSubmit()
    }

export const createImageObjectAction =
    (data: CreateImageT, onCreate: (id: string) => void): AppThunkT =>
    async (dispatch) => {
        dispatch(createImageAction(data))
        onCreate(data.object.id)
    }

export const updateObjectAnimationAction =
    (data: AnimationUpdT, onRefresh: () => void): AppThunkT =>
    async (dispatch) => {
        dispatch(updateAnimationAction(data))
        onRefresh()
    }

export const duplicateObjects =
    (data: AnySceneObjectT[]): AppThunkT =>
    async (dispatch) => {
        const newData = data.map((x) => {
            const newId = "i" + uuidv4()
            return { ...x, id: newId }
        })
        dispatch(duplicateObjectsAction(newData))
        dispatch(setSelectedObjectIdsAction({ objectIds: newData.map((o) => o.id) }))
    }
export const groupObjectsActionAsync =
    (
        groupObject: AnySceneObjectT,
        objects: AnySceneObjectT[],
        wrapperObject: ObjectStyles,
        allObjectsInWrapper: ObjectStylesWithId[]
    ): AppThunkT =>
    async (dispatch, getState) => {
        // Guard: block grouping if any selected object (or its descendants) uses
        // position: static (flex-layout children). Grouping relies on absolute
        // top/left and would silently produce an empty wrapper.
        const allObjs = selectUndoable(getState()).objects.value
        if (objects.some((obj) => hasStaticDescendant(obj, allObjs))) {
            console.warn("Cannot group objects containing flex-layout children (position: static).")
            return
        }

        dispatch(groupObjectsAction({ objects, groupObject, wrapperObject, allObjectsInWrapper }))
        dispatch(setActiveObjectAction({ id: groupObject.id }))
    }

export const addObjectAction =
    (object: AnySceneObjectT): AppThunkT =>
    (dispatch, getState) => {
        return dispatch(addObjAction({ object: object }))
    }

export const addMultiObjectsAction =
    (objects: AnySceneObjectT[]): AppThunkT =>
    (dispatch) => {
        return dispatch(addMultiObjAction({ objects: objects }))
    }
export const delMultiObjectsAction =
    (objects: AnySceneObjectT[]): AppThunkT =>
    (dispatch) => {
        return dispatch(delMultiObjAction({ objects: objects }))
    }

export const checkIndexes =
    (parentId: string | null): AppThunkT =>
    (dispatch, getState) => {
        const { objects } = selectUndoable(getState())

        // pokud nejsou zadne objekty ke kontrole
        if (Object.keys(objects.value).length === 0) return

        let siblings: AnySceneObjectT[]

        if (parentId) {
            const parentChildIds: string[] = selectObjectById(objects, parentId).childIds
            // pokud nejsou zadne indexy ke kontrole
            if (parentChildIds.length === 0) return
            // najdi vsechny sourozence
            siblings = parentChildIds.map(
                (id) => selectObjects(objects).filter((object) => object.id === id)[0]
            )
        } else {
            siblings = selectObjects(objects).filter((object) => object.parentId === null)
        }

        // serad je podle indexu
        const orderedSiblings: AnySceneObjectT[] = sortObjects(siblings)

        // pokud se jejich index nerovna poradi, updatni jeho index
        orderedSiblings.forEach((sibling, index) => {
            if (sibling.index !== index) updIndexAction({ id: sibling.id, index: index })
        })
    }

export const filterAndSortSiblings = (
    objects: AnySceneObjectT[],
    parentId: string | null
): AnySceneObjectT[] => {
    // copy: sortObjects sorts in place and callers pass memoized selector results
    const sortedObjects: AnySceneObjectT[] = sortObjects([...objects])
    return sortedObjects.filter((sortedObject) => sortedObject.parentId === parentId)
}

export const filterObjectsByType = (
    objects: AnySceneObjectT[],
    type: string,
    isGroup?: boolean
): AnySceneObjectT[] => {
    return objects.filter((object) =>
        isGroup ? object.isGroup === isGroup : object.type === type && object.isGroup === false
    )
}

export const getObjectByTitle = (
    objects: AnySceneObjectT[],
    title: string
): AnySceneObjectT | undefined => {
    return objects.find((object) => object.title === title)
}

const selectObjectsMap = (objects: ObjectsStateT) => objects.value

/**
 * Memoized object selectors.
 *
 * `useAppSelector` compares by reference, so a selector that builds a fresh array on every
 * call always reports a change — the component then re-renders on EVERY action dispatched
 * anywhere in the store, even ones that never touch objects (setCanvasCursor, setIsPlaying,
 * setTimeToSeek, …). `createSelector` recomputes only when `objects.value` really changes.
 *
 * WARNING: `sortObjects` from tweenly sorts IN PLACE and returns the same array, so every
 * sorting selector must be given its own copy. Without the copy it would reorder the array
 * memoized by `selectObjects` and that change would leak to all of its consumers.
 */
export const selectObjects = createSelector([selectObjectsMap], (map): AnySceneObjectT[] =>
    Object.values(map)
)

export const selectRootObjects = createSelector([selectObjects], (objects): AnySceneObjectT[] =>
    objects.filter((object) => object.parentId === null)
)

export const selectSortedObjects = createSelector([selectObjects], (objects): AnySceneObjectT[] =>
    sortObjects([...objects])
)

export const selectSortedRootObjects = createSelector(
    [selectSortedObjects],
    (sortedObjects): AnySceneObjectT[] => sortedObjects.filter((object) => object.parentId === null)
)

/**
 * Memoized so that `useObjectActions` — instantiated once per canvas item, per canvas
 * container and per tree node — can expose it as a plain boolean without every instance
 * re-scanning the scene (or re-rendering) on each object change.
 */
export const selectVideoPanelExists = createSelector([selectObjects], (objects): boolean =>
    objects.some((object) => object.type === "video-panel")
)

/**
 * Ids of objects that contain a `position: static` (flex-laid-out) descendant.
 *
 * Memoized on purpose: this was computed inline inside a `useAppSelector` in
 * `ObjectsTreeNodeMenu` and `CanvasItem`. The result is a stable boolean, so it did not cause
 * re-renders — but the selector body runs on EVERY dispatched action, once per tree node and
 * once per canvas item, each time walking that object's whole subtree. One pass over the
 * scene per objects change replaces a few hundred subtree walks per action.
 */
export const selectObjectIdsWithStaticDescendant = createSelector(
    [selectObjectsMap],
    (objectsById): ReadonlySet<string> => {
        const ids = new Set<string>()
        Object.values(objectsById).forEach((object) => {
            if (object && hasStaticDescendant(object, objectsById)) ids.add(object.id)
        })
        return ids
    }
)

export const selectObjectById = (objects: ObjectsStateT, id: string | null): AnySceneObjectT => {
    return id ? objects.value[id] : objects.value[""]
}

export const selectObjectsByIds = (objects: ObjectsStateT, ids: string[]): AnySceneObjectT[] =>
    ids.map((id) => selectObjectById(objects, id))

export const clearTmpObjectsAsync =
    (graphicId: string): AppThunkT =>
    async (dispatch, getState) => {
        try {
            const tmpIds = selectUndoable(getState()).objects.tmpIds
            if (tmpIds.length > 0) {
                await del("files", undefined, {
                    key: `${S3_PREFIX}/${graphicId}/(images|sequences)/(${tmpIds.join("|")})`,
                })
            }
        } catch (error: any) {
            console.error(error)
        } finally {
            dispatch(resetTmpIds())
        }
    }
export const uploadObjectsAndAnimationsAsync =
    (objects: AnySceneObjectT[], animations: AnimationI[]): AppThunkT =>
    async (dispatch) => {
        //add all missing styles and attributes that are defined by a default object ... the object to be uploaded may miss some
        //properties which were defined by further development
        let upgraded = false
        objects.map((object) => {
            const defaultObjectValue = createObject([], defaultObject(object.type), null)
            defaultObjectValue.attributes.forEach((attribute) => {
                if (
                    !object.attributes.find((attr) => {
                        return attr.property === attribute.property
                    })
                ) {
                    object.attributes.push(attribute)
                    upgraded = true
                }
            })
            defaultObjectValue.styles.forEach((style) => {
                if (
                    !object.styles.find((stl) => {
                        return stl.property === style.property
                    })
                ) {
                    object.styles.push(style)
                    upgraded = true
                }
            })
            defaultObjectValue.filters.forEach((filter) => {
                if (
                    !object.filters.find((flt) => {
                        return flt.property === filter.property
                    })
                ) {
                    object.filters.push(filter)
                    upgraded = true
                }
            })
            defaultObjectValue.transforms.forEach((transform) => {
                if (
                    !object.transforms.find((tran) => {
                        return tran.property === transform.property
                    })
                ) {
                    object.transforms.push(transform)
                    upgraded = true
                }
            })
            return object
        })
        const { info } = useToast()
        if (upgraded) info("editor:graphicsUpgradedOnUpload")

        dispatch(uploadObjectsAndAnimationsAction({ objects, animations }))
    }

export const {
    uploadObjectsAndAnimationsAction,
    addObjAction,
    addMultiObjAction,
    updObjectAction,
    setObjectsExpandedAction,
    updateObjectStylesAction,
    updateObjectsStylePropertyAction,
    updateObjectsDifferentStylesPropertyAction,
    updateObjectsPositionAction,
    updateObjectLockAspectRationPropertyAction,
    updateObjectsLockAspectRationPropertyAction,
    updateObjectVisibilityStylesAction,
    duplicateObjectsAction,
    groupObjectsAction,
    unGroupObjectsAction,
    updIndexAction,
    updParentIdAction,
    addChildIdAction,
    delChildIdAction,
    delMultiObjAction,
    delAnimationIdAction,
    addGroupStyleIdAction,
    delGroupStyleIdAction,
    delObjectAction,
    clearObjectsAction,
    moveSelectedHereAction,
    moveSelectedToRootAction,
    moveObjectsAction,
    pushTmpIds,
    resetTmpIds,
    addAnimationAction,
    addAutoAnimationsAction,
    updateAnimationAction,
    createSequenceAnimationsAction,
    createImageAction,
    duplicateAnimationsAction,
    deleteAnimationsAction,
    durationAnimationAction,
    durationAnimationsAction,
    relativeDurationAnimationAction,
    relativeDurationAnimationsAction,
    startOffsetAnimationAction,
    startOffsetAnimationsAction,
    moveOffsetAnimationsAction,
    createTickerStructureAction,
    addSubTimelineAnimationAction,
    updateTickerAnimationAction,
    addTextScrollUpDownAction,
    addTextScrollUpAction,
    addTextScrollDownAction,
    addTextCrawlContinuousAction,
    addTextCrawlStaggeredAction,
    updateScrollPropertyAction,
    updateCrawlPropertyAction,
    updateTickerTextsPropertyAction,
    updateTickerTextsAttributeAction,
    updateTickerStyleWithoutRestartAction,
    updateTickerObjectWithoutRestartAction,
    updateTickerTextsStyleWithoutRestartAction,
    deleteTickerTextAction,
    updateTextCrawlAction,
    resizeCrawlTickerStylesAction,
    resizeScrollTickerStylesAction,
} = objectsSlice.actions

// Thunk action pro aktualizaci stylů textu v tickeru včetně DOM kopií
export const updateTickerTextsStyleWithCopiesAction =
    (payload: UpdateTickerTextsStylePayload): AppThunkT =>
    (dispatch) => {
        // Aktualizovat Redux state
        dispatch(updateTickerTextsStyleWithoutRestartAction(payload))

        // Aktualizovat DOM kopie (pro continuous crawl ticker)
        updateTickerCopiesStyles(payload.textObjects)
    }

export default objectsSlice.reducer
